import { DOCUMENT, Location } from '@angular/common';
import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationError, NavigationStart, Router, UrlSerializer } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { filter } from 'rxjs/operators';

import { routePath } from '../routing/route-scroll-reset';
import { NavigationService } from './navigation.service';

// Resuming the app checks for a new deploy at most this often.
export const UPDATE_CHECK_INTERVAL_MS = 60_000;
// A load failure that recurs this soon after a recovery reload is a broken deploy, not a stale bundle.
export const RELOAD_LOOP_WINDOW_MS = 30_000;
const RELOAD_ATTEMPT_KEY = 'budgets:stale-bundle-reload';

// Safari, Chrome, and Firefox wording for a lazy route bundle that failed to import.
const CHUNK_LOAD_ERROR =
  /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i;
const MAIN_BUNDLE = /(?:^|\/)main(?:-\w+)?\.js$/;

export function isChunkLoadError(error: unknown): boolean {
  return CHUNK_LOAD_ERROR.test(error instanceof Error ? error.message : String(error));
}

function mainBundle(root: ParentNode): string | undefined {
  return Array.from(root.querySelectorAll('script[src]'), script => script.getAttribute('src') ?? '').find(src =>
    MAIN_BUNDLE.test(src),
  );
}

/**
 * Keeps a long-lived page on a deployable version. Each deploy replaces every hashed
 * bundle, so a page left open across one (an iOS app resumed from the background) can
 * no longer load its lazy routes. The iOS shell has no service worker to keep the old
 * bundles, so this also compares the deployed index with the running one on resume.
 * A detected update is applied by a full page load on the next change of page.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly document = inject(DOCUMENT);
  private readonly location = inject(Location);
  private readonly navigation = inject(NavigationService);
  private readonly router = inject(Router);
  private readonly serializer = inject(UrlSerializer);
  private readonly updates = inject(SwUpdate);

  private readonly available = signal(false);
  readonly updateAvailable = this.available.asReadonly();
  private lastCheck = Number.NEGATIVE_INFINITY;
  private latestNavigationId = 0;

  constructor() {
    const destroyRef = inject(DestroyRef);

    if (this.updates.isEnabled) {
      this.updates.versionUpdates
        .pipe(
          filter(event => event.type === 'VERSION_READY'),
          takeUntilDestroyed(destroyRef),
        )
        .subscribe(() => this.available.set(true));
      this.updates.unrecoverable.pipe(takeUntilDestroyed(destroyRef)).subscribe(() => this.navigation.reload());
    }

    this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe(event => {
        this.latestNavigationId = event.id;
        this.applyOnPageChange(event);
      });

    const onVisibilityChange = () => {
      if (this.document.visibilityState === 'visible') void this.checkForUpdate();
    };
    this.document.addEventListener('visibilitychange', onVisibilityChange);
    destroyRef.onDestroy(() => this.document.removeEventListener('visibilitychange', onVisibilityChange));

    void this.checkForUpdate();
  }

  async checkForUpdate(): Promise<void> {
    const now = Date.now();
    if (this.available() || now - this.lastCheck < UPDATE_CHECK_INTERVAL_MS) return;
    this.lastCheck = now;

    try {
      if (this.updates.isEnabled) {
        // A found update arrives through versionUpdates once it is ready.
        await this.updates.checkForUpdate();
        return;
      }

      if (await this.deployChanged()) this.available.set(true);
    } catch {
      // Offline or mid-deploy; the next resume checks again.
    }
  }

  async applyUpdate(): Promise<void> {
    if (this.updates.isEnabled) await this.updates.activateUpdate();
    this.navigation.reload();
  }

  /**
   * Recovers a navigation whose lazy bundle a deploy removed by loading the target page
   * from the server. Offline, the import fails the same way, so the page reloads only
   * once the deployed index confirms a new version, and at most once per loop window.
   */
  recoverFromLoadFailure(error: NavigationError): void {
    if (!isChunkLoadError(error.error)) return;
    const url = this.location.prepareExternalUrl(error.url);

    void this.deployChanged()
      .catch(() => false)
      .then(changed => {
        if (!changed) return;
        this.available.set(true);
        // A navigation started during the check supersedes the failed one; the next
        // page change applies the update instead.
        if (this.latestNavigationId !== error.id || !this.claimRecoveryReload()) return;
        this.navigation.loadDocument(url);
      });
  }

  /** Whether the deployed index loads a different main bundle than this page. Throws offline. */
  private async deployChanged(): Promise<boolean> {
    const running = mainBundle(this.document);
    if (!running) return false;
    // ngsw-bypass keeps a service worker from answering with its cached index.
    const response = await fetch(this.document.baseURI, { cache: 'no-store', headers: { 'ngsw-bypass': 'true' } });
    if (!response.ok) return false;
    const deployed = mainBundle(new DOMParser().parseFromString(await response.text(), 'text/html'));
    return !!deployed && deployed !== running;
  }

  private applyOnPageChange(event: NavigationStart): void {
    // The first navigation is the document load itself, which already has the latest version.
    if (!this.available() || event.id === 1) return;
    if (routePath(this.serializer, event.url) === routePath(this.serializer, this.router.url)) return;

    this.router.currentNavigation()?.abort();
    // Aborting Back or Forward rewrites the target history entry to the page being left,
    // so load the target over that entry instead of adding one.
    this.navigation.loadDocument(this.location.prepareExternalUrl(event.url), {
      replace: event.navigationTrigger === 'popstate',
    });
  }

  private claimRecoveryReload(): boolean {
    try {
      const storage = this.document.defaultView?.sessionStorage;
      if (!storage) return false;
      const now = Date.now();
      if (now - Number(storage.getItem(RELOAD_ATTEMPT_KEY)) < RELOAD_LOOP_WINDOW_MS) return false;
      storage.setItem(RELOAD_ATTEMPT_KEY, String(now));
      return true;
    } catch {
      // Without storage a reload cannot be bounded, so leave the failure to the router.
      return false;
    }
  }
}
